import * as loadjs from 'loadjs'
import * as muteOn from '../../assets/mute-on.svg'
import * as muteOff from '../../assets/mute-off.svg'
import { IBidResponse } from '../../typings/IPrebid'
import Logger from '../helpers/Logger'
import { getQueryParam } from '../helpers/getQueryParam'
import { Device } from '../helpers'
import { addClass } from '../helpers/DOM'
import EventManager from '../eventManager/EventManager'
import { IMA_VIDEO_LOAD_TIMEOUT } from '../constants/video'
import { Slot } from '../slots/Slot'
import { getOutstreamPlayerSize } from './hasOutstream'
import { AuctionResults } from '../prebidFacade/AuctionResults'

const loglevel = getQueryParam('loglevel', window.location.search)
export const IMA_CDN =
  loglevel === 'debug'
    ? 'https://imasdk.googleapis.com/js/sdkloader/ima3_debug.js'
    : 'https://imasdk.googleapis.com/js/sdkloader/ima3.js'

const muteOnHtml = `
    <span class='mv-mute-button' role='button' aria-label='Unmute ad' >
        <object id="iconUnmuteAd" data=${muteOn} type="image/svg+xml">
          Unmute Ad
        </object>
      </span>
  `
const muteOffHtml = `
  <span class='mv-mute-button' role='button' aria-label='Mute ad' >
      <object id="iconMuteAd" data=${muteOff} type="image/svg+xml">
        Mute Ad
      </object>
    </span>
`

export default class OutstreamPlayer {
  autoplayAllowed = true
  adDisplayContainer: google.ima.AdDisplayContainer
  adsLoader: google.ima.AdsLoader
  adsManager: google.ima.AdsManager

  adsInitialized: boolean
  muteButton: HTMLElement
  playAdsObserver: IntersectionObserver
  currentBid: IBidResponse
  slotElement: HTMLElement

  width: number
  height: number

  adPlayStatus: 'start' | 'resume' | 'pause' | 'pre' = 'pre'
  isVisible = false

  constructor(private slot: Slot) {
    this.slotElement = slot.wrapper
    const [contentWidth, contentHeight] = getOutstreamPlayerSize(
      slot,
      slot.wrapper.parentElement
    )
    this.height = contentHeight
    this.width = contentWidth

    // Settings
    const vpaidMode = Device.isIOS
      ? window.google.ima.ImaSdkSettings.VpaidMode.ENABLED
      : window.google.ima.ImaSdkSettings.VpaidMode.INSECURE
    window.google.ima.settings.setVpaidMode(vpaidMode)
    window.google.ima.settings.setDisableCustomPlaybackForIOS10Plus(true)

    this.createMuteButton()

    // Create the ad display container.
    this.adDisplayContainer = new window.google.ima.AdDisplayContainer(
      this.slotElement
    )
    // Create ads loader.
    this.adsLoader = new window.google.ima.AdsLoader(this.adDisplayContainer)

    // Listen and respond to ads loaded and error events.
    this.adsLoader.addEventListener(
      window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      this.onAdsManagerLoadedHandler,
      false
    )
    this.adsLoader.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      this.onAdErrorHandler,
      false
    )
    if (!this.adsInitialized) {
      this.adDisplayContainer.initialize()
      this.adsInitialized = true
    }
    this.playAdsObserver = new IntersectionObserver(this.playAdsHandler, {
      rootMargin: `0px 0px 0px 0px`,
      threshold: 0.51
    })
    this.playAdsObserver.observe(this.slotElement)
  }

  /**
   * After an outstream bid has won an auction, this function
   * will take the bid and setup the OutstreamPlayer to play the ad.
   */
  initOutstreamAd(outstreamBid: IBidResponse) {
    this.currentBid = outstreamBid

    // Request video ads.
    const adsRequest = new window.google.ima.AdsRequest()

    // Sometimes 'ad' doesn't exist, so fallback to vastXml
    adsRequest.adsResponse = outstreamBid.ad || outstreamBid.vastXml

    // IndexExchange only returns `vastUrl`
    if (!adsRequest.adsResponse) {
      adsRequest.adTagUrl = outstreamBid.vastUrl
    }

    // Specify the linear and nonlinear slot sizes. This helps the SDK to
    // select the correct creative if multiple are returned.
    adsRequest.linearAdSlotWidth = this.width
    adsRequest.linearAdSlotHeight = this.height
    adsRequest.nonLinearAdSlotWidth = this.width
    adsRequest.nonLinearAdSlotHeight = this.height

    adsRequest.setAdWillAutoPlay(true)
    adsRequest.setAdWillPlayMuted(true)

    this.adPlayStatus = 'pre'
    this.adsLoader.requestAds(adsRequest)
  }

  private createMuteButton() {
    this.muteButton = document.createElement('div')
    this.muteButton.innerHTML = muteOnHtml
    this.muteButton.style.display = 'none'
    this.muteButton.addEventListener('click', this.handleMuteClick.bind(this))
    addClass(this.muteButton, 'mv-outstream-mute')
    this.slotElement.insertAdjacentElement('afterbegin', this.muteButton)
  }

  private handleMuteClick() {
    const currentVolume = this.adsManager.getVolume()
    if (currentVolume === 0) {
      const volumeValue = 0.8
      this.adsManager.setVolume(volumeValue)
      this.muteButton.innerHTML = muteOffHtml
    } else {
      this.adsManager.setVolume(0)
      this.muteButton.innerHTML = muteOnHtml
    }
  }

  /**
   * This function will be called when the current ad needs to be cleaned up.
   * e.g.: When the slot is about to be refreshed
   */
  cleanup(): void {
    if (this.adsManager) {
      this.adsManager.destroy()
    }
    this.muteButton.style.display = 'none'
  }

  private onAdsManagerLoadedHandler = this.onAdsManagerLoaded.bind(this)
  private onAdsManagerLoaded(
    adsManagerLoadedEvent: google.ima.AdsManagerLoadedEvent
  ) {
    // Get the ads manager.
    const adsRenderingSettings = new window.google.ima.AdsRenderingSettings()
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true
    adsRenderingSettings.uiElements = [
      google.ima.UiElements.AD_ATTRIBUTION,
      google.ima.UiElements.COUNTDOWN
    ]
    adsRenderingSettings.loadVideoTimeout = IMA_VIDEO_LOAD_TIMEOUT

    const videoContentSpoof = { currentTime: 0, duration: 10 }
    this.adsManager = adsManagerLoadedEvent.getAdsManager(
      videoContentSpoof,
      adsRenderingSettings
    )
    this.adsManager.setVolume(0)
    this.muteButton.innerHTML = muteOnHtml
    this.muteButton.style.display = 'block'
    this.slotElement.style.display = 'block'

    // Add listeners to the required events.
    this.adsManager.addEventListener(
      window.google.ima.AdErrorEvent.Type.AD_ERROR,
      this.onAdErrorHandler
    )
    this.adsManager.addEventListener(
      window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
      this.onAdEventHandler
    )

    // Listen to any additional events, if necessary.
    this.adsManager.addEventListener(
      window.google.ima.AdEvent.Type.STARTED,
      this.onAdEventHandler
    )

    try {
      // Initialize the ads manager. Ad rules playlist will start at this time.
      this.adsManager.init(
        this.width,
        this.height,
        window.google.ima.ViewMode.NORMAL
      )

      // Call play to start showing the ad. Single video and overlay ads will
      // start at this time the call will be ignored for ad rules.
      this.adsManager.start()
      this.adPlayStatus = 'start'
    } catch (adError) {
      // An error may be thrown if there was a problem with the VAST response.
      Logger.debug('playAds Error: ', adError)
    }
  }

  private playAdsHandler = this.playAds.bind(this)
  private playAds(
    entries: IntersectionObserverEntry[],
    observer: IntersectionObserver
  ): void {
    entries.forEach((entry) => {
      this.isVisible = entry.isIntersecting
      if (this.adsManager) {
        if (entry.isIntersecting) {
          // Play ad when it comes into view
          this.adsManager.resume()
          this.adPlayStatus = 'resume'
        } else {
          // Pause ad when users scrolls away
          this.adsManager.pause()
          this.adPlayStatus = 'pause'
        }
      }
    })
  }

  private onAdErrorHandler = this.onAdError.bind(this)
  private onAdError(adErrorEvent: google.ima.AdErrorEvent) {
    // Handle the error logging.
    const adError = adErrorEvent.getError()
    Logger.debug(adError)

    EventManager.trigger(EventManager.events.outstreamAdError, {
      adError,
      bidResponse: this.currentBid
    })

    if (this.adsManager) {
      this.adsManager.destroy()
    }

    // This will trigger a refresh without doing a new auction, relying on what is in the AuctionHouse
    const auctionResultSet = [
      new AuctionResults(this.slot.model, this.slot, {})
    ]
    EventManager.trigger(EventManager.events.bidReady, auctionResultSet)
  }

  private onAdEventHandler = this.onAdEvent.bind(this)
  private onAdEvent(adEvent: google.ima.AdEvent) {
    Logger.debug('AD EVENT: ', adEvent.type, adEvent)

    switch (adEvent.type) {
      case window.google.ima.AdEvent.Type.STARTED:
        // We only want to play the first second of an ad if it is off the screen
        // this.adPlayStatus will be 'resume' if the ad has been on screen
        setTimeout(() => {
          if (this.adPlayStatus !== 'resume' && this.isVisible === false) {
            this.adsManager.pause()
            this.adPlayStatus = 'pause'
          }
        }, 1000)
        break
      case window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED:
        // Refresh slot NOW if the ad has ended
        EventManager.trigger(EventManager.events.shortCircuitRefresh, this.slot)
        break
    }
  }
}

export function initOutstream() {
  loadjs(IMA_CDN, 'ima-sdk')
  loadjs.ready('ima-sdk', {
    success: () => {
      // good?
      Logger.debug('IMA LOADED', IMA_CDN)
    },
    error: (e) => console.error('App assets failed to load:', e)
  })
}
